'use client';
import React, { useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { mutate } from 'swr';
import type { Entry } from 'contentful';

import { ExtendedProductFields } from '@/core/contentfulConfig';
import { addToCart } from '@/lib/cart.browser';
import { TW_PRODUCT_MERCH_CARD_CLASSES } from '@/core/constants';
import { Button, Badge, HeaderText, LuvImage } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';

import { LearnMoreModal } from '@/components/common';

import { TypeHolidayBundlesCardFields } from '@/core/generated-types';
import {
  getBundleAvailability,
  generateCartItems,
  redirectAfterAddToCart,
  generatePlayKitchenItem,
} from './utils';
import { CTA_COLORS } from '../types';

function HolidayBundlesCard({
  title,
  image,
  description,
  learnMoreDescription,
  learnMoreButtonText,
  bundleImages,
  price,
  maxPrice,
  originalPrice,
  includes,
  buttonText,
  includesLabel,
  badge,
  includePlayKits,
  includeBookBundles = false,
  outOfStock,
  outOfStockButtonText = 'Sold out',
}: TypeHolidayBundlesCardFields) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const playKitchenProduct = includes.find(
    (item) =>
      item.fields.shopifyProduct?.tags.includes('the-play-kitchen') &&
      item.fields.shopifyProduct?.variants?.length > 1,
  ) as Entry<ExtendedProductFields> | undefined;

  const variants =
    (playKitchenProduct &&
      playKitchenProduct.fields?.shopifyProduct?.variants) ??
    [];
  const [selectedVariant, setSelectedVariant] = useState(variants?.[0]);

  const isBundleUnavailable = outOfStock
    ? outOfStock
    : getBundleAvailability(includes);

  const pricingCopy = maxPrice ? `${price} - ${maxPrice}` : `${price}`;
  const quantity = 1;

  const onAddToCart = async () => {
    setLoading(true);
    const cartItems = generateCartItems(includes, quantity);

    try {
      await addToCart(cartItems, quantity, { noredirect: 1 });
      await mutate('/cart');
      if (playKitchenProduct) {
        setLoading(false);
        setIsOpen(true);
      } else {
        redirectAfterAddToCart(router, includePlayKits, includeBookBundles);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding products to cart:', error);
    }
  };

  const onModalAddToCart = async () => {
    setLoadingModal(true);
    const cartItems = generateCartItems(includes, quantity);
    const playKitchenItem = generatePlayKitchenItem(selectedVariant, quantity);

    const bundleItems = playKitchenProduct ? [playKitchenItem] : [...cartItems];

    try {
      await addToCart(bundleItems, quantity, { noredirect: 1 });
      await mutate('/cart');
      redirectAfterAddToCart(router, includePlayKits, includeBookBundles);
      setLoadingModal(false);
    } catch (error) {
      setLoadingModal(false);
      console.error('Error adding products to cart:', error);
    }
  };

  return (
    <>
      <div
        className={classNames(
          'rounded bg-white drop-shadow-md flex flex-col',
          TW_PRODUCT_MERCH_CARD_CLASSES.container,
        )}
      >
        <div className="relative">
          {badge && (
            <div className="absolute left-4 top-5 z-10">
              <Badge {...badge.fields} />
            </div>
          )}
          <figure className="rounded-t">
            <LuvImage
              height={240}
              width={360}
              imageData={image}
              alt={image?.fields?.altText || ''}
              className={classNames(
                'w-full rounded-t',
                TW_PRODUCT_MERCH_CARD_CLASSES.image,
              )}
            />
          </figure>
        </div>
        <div className="mx-6 flex flex-col grow justify-between">
          <div className="mt-6">
            <HeaderText size={HeaderSize.H3} text={title} align="left" />
            <div className="text-left mt-2 mb-3">
              <p className="m-0 inline text-base">{description}</p>
              <button
                className="inline underline font-bold ml-1"
                onClick={() => setIsOpen(true)}
              >
                {learnMoreButtonText}
              </button>
            </div>
            {includes && (
              <div
                className="flex grow flex-col"
                data-testid="product-includes-container"
              >
                <p className="text-left text-lg font-bold">{includesLabel}</p>
                {includes.map((item) => {
                  return (
                    <ul
                      className="mt-2 pl-[14px] list-disc flex items-start"
                      key={item.sys.id}
                    >
                      {item.sys.contentType.sys.id ===
                        'iconShortDescription' && (
                        <li className="text-base">{item.fields.description}</li>
                      )}
                    </ul>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-4 mb-6">
            <div className="flex items-baseline gap-2 mb-3">
              <p className="text-h2 font-bold text-red-v2">{pricingCopy}</p>
              <p className="text-h3 font-bold text-grey-light line-through">
                {originalPrice}
              </p>
            </div>
            <Button
              ctaColor={CTA_COLORS.RED}
              onClick={onAddToCart}
              ctaText={isBundleUnavailable ? outOfStockButtonText : buttonText}
              disabled={loading || isBundleUnavailable}
              includeSpinner={loading}
              classes="disabled:bg-grey-dark disabled:border-grey-dark disabled:opacity-100"
              fullWidth
            />
          </div>
        </div>
      </div>
      <LearnMoreModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        title={title}
        description={learnMoreDescription}
        badge={badge}
        image={image}
        bundleImages={bundleImages}
        price={price}
        originalPrice={originalPrice}
        maxPrice={maxPrice}
        loading={loadingModal}
        buttonText={buttonText}
        buttonColor={CTA_COLORS.RED}
        handleClick={onModalAddToCart}
        product={playKitchenProduct}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        isBundleUnavailable={isBundleUnavailable}
        outOfStockButtonText={outOfStockButtonText}
      />
    </>
  );
}

export default HolidayBundlesCard;
